import React, { useEffect, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { getConversasion, deleteConversation } from '../../utils/API';
import { useNavigate } from 'react-router-dom';
import { Modal, message } from 'antd';

interface Conversation {
  id: string;
  title: string;
  createdAt?: string;
}

interface SidebarProps {
  onClose?: () => void;
  /** Currently active chat ID — controlled by parent Chatbot */
  selectedChatId?: string | null;
  /** Called when user picks a chat — parent loads it without full re-render */
  onChatSelect?: (chatId: string) => void;
  /** Called when user clicks New Chat */
  onNewChat?: () => void;
}

/* ── Three-dot animated loader ──────────────────────────────────────────── */
const ThreeDotLoader: React.FC = () => (
  <div className="flex items-center gap-1.5 py-1 px-0.5">
    <span className="chat-dot" />
    <span className="chat-dot" />
    <span className="chat-dot" />
  </div>
);

const Sidebar: React.FC<SidebarProps> = ({ onClose, selectedChatId, onChatSelect, onNewChat }) => {
  const [history, setHistory] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);
  const navigate = useNavigate();

  const filteredHistory = history.filter((item) =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const today = new Date();
    const diffDays = Math.floor(
      (today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";

    return date.toLocaleDateString();
  };

  const handleDelete = (id: string) => {
    setItemToDelete(id);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    const deletedChatId = itemToDelete;

    try {
      await deleteConversation(deletedChatId);
      setHistory((prev) => prev.filter((item) => item.id !== deletedChatId));
      setDeleteModalOpen(false);
      message.success("Conversation deleted");

      if (deletedChatId === selectedChatId) {
        if (onNewChat) {
          onNewChat();
        } else {
          navigate("/chat", { replace: true });
        }
      }
    } catch (err) {
      console.error("Delete failed:", err);
      message.error("Failed to delete conversation");
    } finally {
      setItemToDelete(null);
      setDeleteModalOpen(false); // Ensure modal is closed
    }
  };

  const cancelDelete = () => {
    setItemToDelete(null);
    setDeleteModalOpen(false);
  };

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const res = await getConversasion();

        let conversations: any[] = [];

        if (Array.isArray(res)) {
          conversations = res;
        } else if (Array.isArray(res?.data)) {
          conversations = res.data;
        } else if (Array.isArray(res?.conversations)) {
          conversations = res.conversations;
        }

        const formatted = conversations.map((item: any, index: number) => ({
          id: item.id || item._id || index.toString(),
          title: item.title || item.name || "Untitled Chat",
          createdAt: item.createdAt || item.created_at || item.date || ""
        }));

        if (!selectedChatId) {
          formatted.unshift({
            id: "temp-new-chat",
            title: "New Chat",
            createdAt: new Date().toISOString()
          });
        }

        setHistory(formatted);
      } catch (err) {
        console.error("Error fetching messages:", err);
        if (!selectedChatId) {
          setHistory([{
            id: "temp-new-chat",
            title: "New Chat",
            createdAt: new Date().toISOString()
          }]);
        } else {
          setHistory([]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [selectedChatId]);

  return (
    <>
      <style>{`
        .chat-sidebar-scroll::-webkit-scrollbar {
          width: 5px;
        }
        .chat-sidebar-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .chat-sidebar-scroll::-webkit-scrollbar-thumb {
          background: #e5e7eb;
          border-radius: 10px;
        }
        .chat-sidebar-scroll::-webkit-scrollbar-thumb:hover {
          background: #d1d5db;
        }
      `}</style>
      <aside
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          background: "#ffffff",
          overflow: "hidden",
        }}
      >
        {/* ── Title ─────────────────────────────────────────────────────── */}
        <div style={{ padding: "18px 16px 10px 16px", flexShrink: 0 }}>
          <h2
            style={{
              margin: 0,
              fontSize: "16px",
              fontWeight: 700,
              color: "#111827",
              lineHeight: 1.3,
            }}
          >
            Chat History
          </h2>
        </div>

        {/* ── Search ────────────────────────────────────────────────────── */}
        <div style={{ padding: "0 12px 10px 12px", flexShrink: 0 }}>
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <Search
              size={14}
              style={{
                position: "absolute",
                left: "10px",
                color: "#9ca3af",
                pointerEvents: "none",
                flexShrink: 0,
              }}
            />
            <input
              type="text"
              placeholder="Search history..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "7px 10px 7px 30px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "12px",
                color: "#374151",
                background: "#f9fafb",
                outline: "none",
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
            />
          </div>
        </div>

        {/* ── New Chat button ────────────────────────────────────────────── */}
        <div style={{ padding: "0 12px 14px 12px", flexShrink: 0 }}>
          <button
            onClick={() => {
              if (onNewChat) {
                onNewChat();
              } else {
                navigate("/chat");
              }
              onClose?.();
            }}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              padding: "9px 12px",
              background: "#0C447C",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "#1B5FA8";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "#0C447C";
            }}
          >
            <Plus size={15} />
            New Chat
          </button>
        </div>

        {/* ── Recent Chats label ─────────────────────────────────────────── */}
        <div style={{ padding: "0 16px 6px 16px", flexShrink: 0 }}>
          <span
            style={{
              fontSize: "10px",
              fontWeight: 700,
              color: "#9ca3af",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
            }}
          >
            Recent Chats
          </span>
        </div>

        {/* ── Chat list ─────────────────────────────────────────────────── */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "12px",
            overscrollBehavior: "contain",
          }}
          className="chat-sidebar-scroll"
        >
          {loading && (
            <div style={{ fontSize: "12px", color: "#9ca3af", padding: "4px 8px" }} className="flex flex-col items-center justify-center mt-20 gap-3">
              <ThreeDotLoader />
              <p>Loading history</p>
            </div>
          )}

          {!loading && history.length === 0 && (
            <p style={{ fontSize: "12px", color: "#9ca3af", padding: "4px 8px" }}>
              No conversations found
            </p>
          )}

          {!loading && history.length > 0 && filteredHistory.length === 0 && (
            <p style={{ fontSize: "12px", color: "#9ca3af", padding: "4px 8px" }}>
              No conversations match your search
            </p>
          )}

          <nav>
            {filteredHistory.map((item) => {
              const isActive = item.id === selectedChatId || (item.id === "temp-new-chat" && !selectedChatId);
              return (
                <div
                  key={item.id}
                  onMouseEnter={(e) => {
                    setHoveredChatId(item.id);
                    if (!isActive)
                      (e.currentTarget as HTMLDivElement).style.background = "#f9fafb";
                  }}
                  onMouseLeave={(e) => {
                    setHoveredChatId((prev) => (prev === item.id ? null : prev));
                    if (!isActive)
                      (e.currentTarget as HTMLDivElement).style.background = "transparent";
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "7px 8px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    marginBottom: "2px",
                    background: isActive ? "#0C447C" : "transparent",
                    transition: "background 0.12s",
                  }}
                >
                  {/* Clickable text area */}
                  <div
                    style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                    onClick={() => {
                      if (item.id === "temp-new-chat") {
                        if (onNewChat) {
                          onNewChat();
                        } else {
                          navigate("/chat");
                        }
                      } else {
                        if (onChatSelect) {
                          onChatSelect(item.id);
                        } else {
                          navigate(`/chat/${item.id}`);
                        }
                      }
                      onClose?.();
                    }}
                  >
                    <p
                      style={{
                        margin: 0,
                        fontSize: "13px",
                        fontWeight: isActive ? 600 : 500,
                        color: isActive ? "#ffffff" : "#111827",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: 1.4,
                      }}
                    >
                      {item.title}
                    </p>
                    {item.createdAt && (
                      <span
                        style={{
                          display: "block",
                          fontSize: "11px",
                          color: isActive ? "rgba(255,255,255,0.7)" : "#9ca3af",
                          marginTop: "2px",
                        }}
                      >
                        {formatDate(item.createdAt)}
                      </span>
                    )}
                  </div>

                  {/* Delete button — dustbin icon */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item.id);
                    }}
                    title="Delete chat"
                    style={{
                      flexShrink: 0,
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      padding: "3px 4px",
                      borderRadius: "6px",
                      color: isActive ? "rgba(255,255,255,0.7)" : "#9ca3af",
                      display: (item.id !== "temp-new-chat" && hoveredChatId === item.id) ? "flex" : "none",
                      alignItems: "center",
                      justifyContent: "center",
                      marginLeft: "4px",
                      transition: "color 0.12s, background 0.12s",
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = "#ef4444";
                      (e.currentTarget as HTMLButtonElement).style.background = "#fee2e2";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.color = "#9ca3af";
                      (e.currentTarget as HTMLButtonElement).style.background = "none";
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 16 16">
                      <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                      <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                    </svg>
                  </button>
                </div>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}  // This makes the modal open when deleteModalOpen is true
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Yes"
        cancelText="No"
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{
          bottom: 20,
          top: "10vw",
          maxHeight: "calc(100vh - 30px)",
          margin: "0 auto",
          position: "relative",
        }}
        title={
          <div
            style={{
              marginLeft: "11px",
              width: "100%",
              paddingRight: 20,
            }}
          >
            Confirmation
          </div>
        }
        okButtonProps={{
          style: {
            backgroundColor: "#ff4d4f",
            borderColor: "#ff4d4f",
            color: "white",
          },
        }}
        bodyStyle={{
          padding: "22px",
          overflowY: "auto",
          fontFamily: "inter, sans-serif",
        }}
        styles={{
          header: {
            borderBottom: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
          content: {
            padding: 0,
            overflow: "hidden",
          },
          footer: {
            borderTop: "1px solid #e0e0e0",
            padding: "12px",
            position: "sticky",
            top: 0,
            backgroundColor: "white",
          },
        }}
      >
        <p>Are you sure you want to delete this chat?</p>
      </Modal>
    </>
  );
};

export default Sidebar;
